import React, { useEffect, useState, useMemo } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  Select,
  Input,
  Tooltip,
  Tag,
  Typography,
  Row,
  Col,
  TableProps,
} from 'antd';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { createStyles } from 'antd-style';
import { useSearchParams } from 'react-router-dom';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import ProductModal from './ProductModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

import CopyButton from '../../../../components/copyButton';
import Barcode from '../../../../components/Barcode';
import ImportProductModal from './ImportProductModal';

import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';

const { Text } = Typography;
const { Option } = Select;
interface BarcodeProps {
  value: string; // the product_code to encode
}
const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Products {
  product_id: number;
  product_name: string;
  product_code: string;
  product_category: number;       // category_id mapped for modal
  product_brand_name: number;     // brand_id mapped for modal
  ut_name: string;                // unit type name
  product_qty: number;
  product_discount_price: number;
  product_unit: number;           // unit type id
  product_unit_value: number;     // numeric value per unit
  product_mrp: number;
  category_name: string;          // display name
  category_id: number;
  brand_id: number;
  brand_name: string;             // display name
  product_price: number;
  product_status: number;         // 0 or 1
  product_size: number;         // 0 or 1
  product_gst: number;         // 0 or 1
  product_hsn_code?: number;
}
const MedicalProducts: React.FC = () => {
  const { categories, loaded } = useAppSelector(state => state.master);
  const [products, setProducts] = useState<Products[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Products | null>(null);
  const { styles } = useStyle();
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [pagination, setPagination] = useState<{ current: number; pageSize: number; total: number }>({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});

  const [searchParams, setSearchParams] = useSearchParams();

  // 2. Read the current filters from URL
  const selectedCategoryId = searchParams.get('category') || undefined;
  const selectedKeyword = searchParams.get('keyword') || '';

  // 3. Local state for controlled inputs
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(selectedCategoryId);
  const [keywordFilter, setKeywordFilter] = useState<string>(selectedKeyword);

  const groupedCategoryOptions = useMemo(() => {
    if (!categories || categories.length === 0) return [];

    const catMap = new Map<number, (typeof categories)[0]>();
    categories.forEach((c) => catMap.set(c.category_id, c));

    // Helper for full path
    const getCategoryPath = (cat: (typeof categories)[0]): string => {
      const path: string[] = [cat.category_name];
      let current = cat;
      let depth = 0;
      while (current.category_parent && current.category_parent !== 0 && depth < 10) {
        const parent = catMap.get(current.category_parent);
        if (!parent || parent.category_id === current.category_id) break;
        path.unshift(parent.category_name);
        current = parent;
        depth++;
      }
      return path.join(' -> ');
    };

    const getRootCategory = (cat: (typeof categories)[0]): (typeof categories)[0] => {
      let current = cat;
      let depth = 0;
      while (current.category_parent && current.category_parent !== 0 && depth < 10) {
        const parent = catMap.get(current.category_parent);
        if (!parent || parent.category_id === current.category_id) break;
        current = parent;
        depth++;
      }
      return current;
    };

    const groupMap = new Map<number, { root: (typeof categories)[0]; items: typeof categories }>();

    categories.forEach((cat) => {
      const root = getRootCategory(cat);
      if (!groupMap.has(root.category_id)) {
        groupMap.set(root.category_id, { root, items: [] });
      }
      groupMap.get(root.category_id)!.items.push(cat);
    });

    const optGroups: { label: string; options: { label: string; value: number }[] }[] = [];

    groupMap.forEach(({ root, items }) => {
      const sortedItems = [...items].sort((a, b) => {
        if (a.category_id === root.category_id) return -1;
        if (b.category_id === root.category_id) return 1;
        return a.category_name.localeCompare(b.category_name);
      });

      const formattedOptions = sortedItems.map((item) => {
        const isRoot = !item.category_parent || item.category_parent === 0;
        const path = getCategoryPath(item);
        return {
          label: isRoot ? `${item.category_name} (Main Category)` : path,
          value: item.category_id,
        };
      });

      optGroups.push({
        label: `📂 ${root.category_name}`,
        options: formattedOptions,
      });
    });

    return optGroups;
  }, [categories]);

  const categoryMap = useMemo(() => {
    const map = new Map<number, (typeof categories)[0]>();
    (categories || []).forEach((c) => map.set(c.category_id, c));
    return map;
  }, [categories]);

  const getCategoryHierarchy = (record: Products) => {
    const catId = record.category_id || (record as any).product_category;
    if (!catId) {
      return {
        categoryType: record.category_name || '-',
        mainCategory: '-',
        subCategory: '-',
      };
    }

    const chain: (typeof categories)[0][] = [];
    let current = categoryMap.get(catId);
    let depth = 0;
    while (current && depth < 10) {
      chain.unshift(current);
      if (!current.category_parent || current.category_parent === 0) break;
      const parent = categoryMap.get(current.category_parent);
      if (!parent || parent.category_id === current.category_id) break;
      current = parent;
      depth++;
    }

    if (chain.length === 0) {
      return {
        categoryType: record.category_name || '-',
        mainCategory: '-',
        subCategory: '-',
      };
    }

    if (chain.length === 1) {
      return {
        categoryType: chain[0].category_name,
        mainCategory: '-',
        subCategory: '-',
      };
    }

    if (chain.length === 2) {
      return {
        categoryType: chain[0].category_name,
        mainCategory: chain[1].category_name,
        subCategory: '-',
      };
    }

    return {
      categoryType: chain[0].category_name,
      mainCategory: chain[1].category_name,
      subCategory: chain.slice(2).map((c) => c.category_name).join(' > '),
    };
  };



  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchProduct = async (
    page = 1,
    limit = 10,
    category_id?: string,
    category_keyword?: string
  ) => {
    let url = `get-medical-products?page=${page}&limit=${limit}`;
    if (category_id) url += `&category_id=${category_id}`;
    if (category_keyword) url += `&keyword=${category_keyword}`;

    const response = await apiRequest({
      method: 'get',
      url,
    });
    setProducts(response.data);
    setPagination({
      current: response.page,
      pageSize: response.limit,
      total: response.total,
    });
  };



  useEffect(() => {
    fetchProduct(pagination.current,
      pagination.pageSize, categoryFilter, keywordFilter);
  }, [searchParams]);

  const handleEditClick = (record: Products) => {
    setIsEditMode(true);
    const rawHsn = (record as any).product_hsn_code ?? (record as any).hc_id ?? (record as any).hsn_id ?? (record as any).product_hsn ?? (record as any).hsn_code;
    setInitialValues({
      product_id: record.product_id,
      product_name: record.product_name,
      product_code: record.product_code,
      product_price: record.product_price,
      product_unit_value: record.product_unit_value,
      product_unit: record.product_unit,
      product_qty: record.product_qty,
      product_mrp: record.product_mrp,
      product_discount_price: record.product_discount_price,
      product_category: record.category_id,      // numeric id for select
      brand_id: record.brand_id,
      product_brand_name: record.brand_id,       // numeric id for select
      ut_name: record.ut_name,
      category_name: record.category_name,
      category_id: record.category_id,
      brand_name: record.brand_name,
      product_status: record.product_status,
      product_size: record.product_size,
      product_gst: record.product_gst,
      product_hsn_code: rawHsn ? (Number(rawHsn) || rawHsn) : undefined,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchProduct(pagination.current, pagination.pageSize, selectedCategoryId, selectedKeyword);
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (product_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-medical-product/${product_id}`,
      });
      showSuccess('Product deleted');
      fetchProduct(pagination.current, pagination.pageSize, selectedCategoryId, selectedKeyword);
    } catch {
      showError('Failed to delete Product');
    }
  };

  const handleStatusToggle = async (productId: number) => {
    setProducts((prev) =>
      prev.map((item) =>
        item.product_id === productId
          ? { ...item, product_status: item.product_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-medical-product/${productId}`,
      });
      showSuccess('Product status updated');
    } catch (error) {
      showError('Failed to update status');
      setProducts((prev) =>
        prev.map((item) =>
          item.product_id === productId
            ? { ...item, product_status: item.product_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Products>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };


  const handleImportSuccess = () => {
    fetchProduct(pagination.current, pagination.pageSize, selectedCategoryId, selectedKeyword);
  };

  const columns: TableColumnsType<Products> = [
    {
      title: 'Product Name',
      dataIndex: 'product_name',
      key: 'product_name',
      filters: Array.from(new Set(products.map((p) => p.product_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filteredValue: filteredInfo.product_name || null,
      onFilter: (value, record) => record.product_name.includes(value as string),
      sorter: (a, b) => a.product_name.localeCompare(b.product_name),
      sortOrder: sortedInfo.columnKey === 'product_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
    },
    {
      title: 'Category',
      key: 'category_name',
      render: (_, record) => {
        const catInfo = getCategoryHierarchy(record);
        return (
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 6, minWidth: 140 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ fontSize: '12px' }}>
                <span style={{ color: '#8c8c8c', fontSize: '11px' }}>Type: </span>
                <span style={{ fontWeight: 600, color: '#1677ff' }}>{catInfo.categoryType || '-'}</span>
              </div>
              <div style={{ fontSize: '12px' }}>
                <span style={{ color: '#8c8c8c', fontSize: '11px' }}>Main: </span>
                <span style={{ fontWeight: 500, color: '#262626' }}>{catInfo.mainCategory || '-'}</span>
              </div>
              <div style={{ fontSize: '12px' }}>
                <span style={{ color: '#8c8c8c', fontSize: '11px' }}>Sub: </span>
                <span style={{ color: '#595959' }}>{catInfo.subCategory || '-'}</span>
              </div>
            </div>
            {record.category_name && <CopyButton textToCopy={record.category_name} />}
          </div>
        );
      },
    },
    {
      title: 'Brand Name ',
      dataIndex: 'brand_name',
      key: 'brand_name',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>{record.brand_name || 'N/A'}</span>
          {record.brand_name && <CopyButton textToCopy={record.brand_name} />}
        </div>
      ),
    },
    // {
    //   title: 'Product Code',
    //   dataIndex: 'product_code',
    //   key: 'product_code',
    //   render: (_, record) => (
    //     <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
    //       <span>{record.product_code || 'N/A'}</span>
    //       {record.product_code && <CopyButton textToCopy={record.product_code} />}
    //     </div>
    //   ),
    // },
    // {
    //   title: 'Bar Code',
    //   dataIndex: 'product_code',
    //   key: 'product_code',
    //   render: (_, record) => (
    //     <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
    //       {record.product_code && <Barcode value={record.product_code} />}
    //     </div>
    //   ),
    // },
    {
      title: 'Price Info',
      dataIndex: 'product_price',
      key: 'product_price',
      width: 180,
      sorter: (a, b) => a.product_price - b.product_price,
      sortOrder: sortedInfo.columnKey === 'product_price' ? sortedInfo.order : null,
      render: (_, record) => (
        <div style={{ lineHeight: '1.4' }}>
          <div><Text type="secondary" style={{ fontSize: '12px' }}>Price:</Text> <Text strong>₹{record.product_price.toFixed(2)}</Text></div>
          <div><Text type="secondary" style={{ fontSize: '12px' }}>MRP:</Text> <Text type="danger" style={{ fontSize: '11px' }}>₹{record.product_mrp.toFixed(2)}</Text></div>
          {record.product_discount_price > 0 && (
            <div><Text type="secondary" style={{ fontSize: '12px' }}>Disc:</Text> <Text type="success" style={{ fontSize: '11px' }}>₹{record.product_discount_price.toFixed(2)}</Text></div>
          )}
        </div>
      ),
    },
    {
      title: 'Stock',
      dataIndex: 'product_qty',
      key: 'product_qty',
      width: 120,
      sortOrder: sortedInfo.columnKey === 'product_qty' ? sortedInfo.order : null,
      render: (_, record) => (
        <Tag color={record.product_qty > 0 ? 'green' : 'red'}>
          {record.product_qty} {record.ut_name}
        </Tag>
      ),
    },
  ];

  // Action Column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 180,
      align: 'center',
      fixed: 'right',
      render: (_: any, record: Products) => (
        <Space size="small">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Tooltip title={record.product_status ? "Deactivate" : "Activate"}>
                <Switch
                  size="small"
                  checked={!!record.product_status}
                  onChange={() => handleStatusToggle(record.product_id)}
                />
              </Tooltip>
              <Tooltip title="Edit Product">
                <Button
                  size="middle"
                  type="text"
                  style={{ color: '#1890ff', backgroundColor: '#e6f7ff' }}
                  icon={<EditOutlined />}
                  onClick={() => handleEditClick(record)}
                />
              </Tooltip>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Tooltip title="Delete Product">
              <Popconfirm
                title="Delete this product?"
                onConfirm={() => handleDelete(record.product_id)}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  size="middle"
                  type="text"
                  danger
                  style={{ backgroundColor: '#fff1f0' }}
                  icon={<DeleteOutlined />}
                />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      ),
    });
  }


  return (
    <div style={{ height: "calc(100vh - 145px)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header Section */}
      <div style={{ padding: '0 16px 16px 16px' }}>
        <Row align="middle" justify="space-between" style={{ marginBottom: 12 }}>
          <Col>
            <Breadcrumb style={{ margin: '8px 0' }}>
              <Breadcrumb.Item href="/"><HomeOutlined /></Breadcrumb.Item>
              <Breadcrumb.Item>View Products</Breadcrumb.Item>
            </Breadcrumb>
          </Col>
          <Col>
            <Space>
              {currentModule?.right_add === 'Y' && (
                <Button type="primary" onClick={showCreateModal}>
                  Add Product
                </Button>
              )}
              {currentModule?.right_import === 'Y' && (
                <Button type="primary" ghost onClick={() => setIsImportModalVisible(true)}>
                  Import Product
                </Button>
              )}
            </Space>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Flex gap="small" wrap align="center">
              <Select
                showSearch
                placeholder="Filter by Category"
                allowClear
                style={{ minWidth: 240 }}
                dropdownMatchSelectWidth={false}
                onChange={(value) => {
                  setCategoryFilter(value);
                  const params = new URLSearchParams(searchParams.toString());
                  if (value) params.set('category', value.toString());
                  else params.delete('category');
                  setSearchParams(params);
                  fetchProduct(1, pagination.pageSize, value, keywordFilter);
                }}
                value={categoryFilter}
                options={groupedCategoryOptions}
                filterOption={(input, option) =>
                  (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                }
              />

              <Input
                placeholder="Search products..."
                style={{ width: 250 }}
                allowClear
                value={keywordFilter}
                onChange={(e) => {
                  const value = e.target.value;
                  setKeywordFilter(value);
                  const params = new URLSearchParams(searchParams.toString());
                  if (value) params.set('keyword', value);
                  else params.delete('keyword');
                  setSearchParams(params);
                  fetchProduct(1, pagination.pageSize, categoryFilter, value);
                }}
              />
            </Flex>
          </Col>
        </Row>
      </div>

      {/* Table Section */}
      <div
        style={{
          flex: 1,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
          margin: '0 16px 16px 16px',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
        }}
      >
        <Table<Products>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="product_id"
          dataSource={products}
          bordered
          size="middle"
          pagination={false}
          scroll={{ x: 1200, y: 'calc(100vh - 420px)' }}
          locale={{ emptyText: 'No product found' }}
          style={{ flex: 1 }}
        />

        <div style={{ marginTop: 16 }}>
          <Flex justify="space-between" align="center">
            <Text type="secondary">
              Showing {(pagination.current - 1) * pagination.pageSize + 1}–
              {Math.min(pagination.current * pagination.pageSize, pagination.total)} of {pagination.total}
            </Text>
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              showSizeChanger
              pageSizeOptions={['10', '20', '50', '100']}
              size="small"
              onChange={(page, pageSize) => {
                setPagination((prev) => ({ ...prev, current: page, pageSize }));
                fetchProduct(page, pageSize, categoryFilter, keywordFilter);
              }}
            />
          </Flex>
        </div>
      </div>
      {isModalVisible &&
        <ProductModal
          visible={isModalVisible}
          onCancel={hideCreateModal}
          onCreate={handleCreate}
          isEditMode={isEditMode}
          initialValues={initialValues}
        />}
      <ImportProductModal
        visible={isImportModalVisible}
        onClose={() => setIsImportModalVisible(false)}
        onSuccess={handleImportSuccess}
      />
    </div>
  );
};

export default MedicalProducts;